import { useMemo } from 'react';
import { Alley } from '../../../../../services/entities/impl/blfApi/schemas/alley';
import { Upload } from '../../../../../services/entities/impl/blfApi/schemas/upload';
import {
  UploadDetail,
  UploadDetailSchema,
  UploadStatus,
} from '../../../../../services/entities/impl/blfApi/schemas/uploadSummary';
import {
  ValidationSummary,
  createValidationSummary,
} from '../../../utils/validation/createValidationSummary';
import { getValidationCounts } from '../../../utils/validation/getValidationCounts';
import {
  ValidationMessage,
  ValidationResult,
  validateSchema,
} from '../../../utils/validation/validateSchema';

// Maximum allowable difference in bag count to account for timing inconsistencies in data fetching / generation of upload detail vs actual upload counts
const BAG_COUNT_TOLERANCE = 1;

/**
 * Hook for validating a specific upload detail
 * Provides schema validation plus additional business rule validations
 */
export const useUploadDetailValidation = (
  uploadDetail: UploadDetail | undefined,
  alleys: Alley[] = [],
  uploads: Upload[] = []
): {
  validationResult: ValidationResult;
  summary: ValidationSummary;
} => {
  return useMemo(() => {
    // Check for empty upload detail data
    if (!uploadDetail) {
      const emptyValidationResult: ValidationResult = {
        valid: false,
        messages: [
          {
            severity: 'error',
            path: '',
            message:
              'No upload detail data found. API endpoint may have failed.',
          },
        ],
      };

      const counts = getValidationCounts(emptyValidationResult);

      return {
        validationResult: emptyValidationResult,
        summary: {
          result: createValidationSummary(counts),
          counts,
        },
      };
    }

    // 1. Validate the upload detail against schema
    const schemaValidationResult = validateSchema(
      uploadDetail,
      UploadDetailSchema
    );

    // 2. Perform additional validations
    const additionalValidationMessages: ValidationMessage[] = [];

    // 2.1 Validate upload progress against actual uploads vs alleys
    if (alleys.length > 0) {
      // Count all bag uploads for alley files
      const bagUploadsCount = uploads.filter((upload) =>
        // Match uploads that belong to this collection's alleys
        alleys.some((alley) => alley.fileName === upload.fileName)
      ).length;

      // Expected files count - just alley bags, no collection.json
      const expectedUploadsCount = alleys.length;

      // Calculate percentage matching backend logic (bags only)
      const calculatedPercentUploaded =
        expectedUploadsCount > 0
          ? Math.round((bagUploadsCount / expectedUploadsCount) * 100)
          : 0;

      // Apply tolerance to account for timing differences in data fetching
      const tolerablePercentDifference =
        expectedUploadsCount > 0
          ? Math.ceil((BAG_COUNT_TOLERANCE / expectedUploadsCount) * 100)
          : 0;

      // Verify reported percent matches calculated percent within tolerance
      if (
        Math.abs(uploadDetail.percentUploaded - calculatedPercentUploaded) >
        tolerablePercentDifference
      ) {
        // Build a descriptive message with all counts
        let message = `Reported Upload Detail progress (${uploadDetail.percentUploaded}%) doesn't match calculated progress (${calculatedPercentUploaded}%).`;

        // Add bag uploads count
        message += ` Found ${bagUploadsCount} out of ${expectedUploadsCount} expected alley bag uploads.`;

        // Clarify the calculation and tolerance
        message += ` The percentage is calculated based on bag files only: ${bagUploadsCount}/${expectedUploadsCount} = ${calculatedPercentUploaded}%.`;
        message += ` (Allowed tolerance: ±${BAG_COUNT_TOLERANCE} bags or ±${tolerablePercentDifference}%)`;

        // Add the validation message
        additionalValidationMessages.push({
          severity: 'warning',
          path: 'percentUploaded',
          message,
        });
      }
    }

    // 2.2 Validate upload timestamps logic
    if (uploadDetail.uploadStartedAt && uploadDetail.latestFileUploadedAt) {
      const startDate = new Date(uploadDetail.uploadStartedAt);
      const latestDate = new Date(uploadDetail.latestFileUploadedAt);

      if (latestDate < startDate) {
        additionalValidationMessages.push({
          severity: 'error',
          path: 'latestFileUploadedAt',
          message: `Latest file upload time (${uploadDetail.latestFileUploadedAt}) is before upload start time (${uploadDetail.uploadStartedAt}).`,
        });
      }
    }

    // 2.3 Check status consistency with percent uploaded
    if (
      uploadDetail.status === UploadStatus.Uploading &&
      uploadDetail.percentUploaded === 100
    ) {
      additionalValidationMessages.push({
        severity: 'warning',
        path: 'status',
        message: `Status is UPLOADING but upload progress is 100%.`,
      });
    }

    if (
      uploadDetail.status === UploadStatus.Completed &&
      uploadDetail.percentUploaded < 100
    ) {
      additionalValidationMessages.push({
        severity: 'error',
        path: 'status',
        message: `Status is COMPLETED but upload progress is only ${uploadDetail.percentUploaded}%.`,
      });
    }

    // 3. Combine validation results
    const combinedMessages = [
      ...schemaValidationResult.messages,
      ...additionalValidationMessages,
    ];

    const validationResult: ValidationResult = {
      valid:
        schemaValidationResult.valid &&
        additionalValidationMessages.length === 0,
      messages: combinedMessages,
    };

    // 4. Get validation counts
    const counts = getValidationCounts(validationResult);

    // 5. Create summary result with messages
    const summaryResult = createValidationSummary(counts);

    return {
      validationResult,
      summary: {
        result: summaryResult,
        counts,
      },
    };
  }, [uploadDetail, alleys, uploads]);
};
